import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BookOpen,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Star,
  Award,
  Bell,
  ChevronRight,
  Play,
  Loader2,
  LogOut,
  ArrowRight,
  Keyboard,
  User,
} from 'lucide-react';
import { createStudentSession, getStudentSession, clearStudentSession } from '../../utils/supabase/auth';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { toast } from 'sonner';

interface StudentActivity {
  id: string;
  lesson_title: string;
  slide_title: string;
  score: number | null;
  graded: boolean;
  created_at: string;
  feedback?: string;
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authAttempted, setAuthAttempted] = useState(false);
  const [studentSession, setStudentSession] = useState<any>(null);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [grade, setGrade] = useState<any>(null);
  const [recentActivities, setRecentActivities] = useState<StudentActivity[]>([]);
  const [sessionCode, setSessionCode] = useState('');
  const [studentName, setStudentName] = useState('');
  const [submissionStats, setSubmissionStats] = useState<{ total: number; graded: number }>({ total: 0, graded: 0 });

  useEffect(() => {
    const session = getStudentSession();
    if (session) {
      loadStudentData(session);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchDashboardData = async (code: string, name: string) => {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-9d7d881a/student/dashboard`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({
          fullName: name.trim(),
          sessionCode: code.trim()
        })
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to load dashboard data');
    }

    return data;
  };

  const loadStudentData = async (sessionOverride?: any) => {
    setLoading(true);
    try {
      // Get student session from localStorage
      const session = sessionOverride || getStudentSession();
      if (!session) {
        setStudentSession(null);
        setSessionInfo(null);
        return;
      }

      // Load session info
      if (!session.sessionCode) {
        toast.error('Invalid session code');
        setStudentSession(null);
        setSessionInfo(null);
        return;
      }
      const dashboardData = await fetchDashboardData(session.sessionCode, session.studentName);

      const updatedSession = await createStudentSession(
        dashboardData.studentName,
        dashboardData.session.id,
        dashboardData.session.session_code
      );

      setStudentSession(updatedSession);
      setSessionInfo(dashboardData.session);
      setGrade(dashboardData.grade);
      setSubmissionStats(dashboardData.submissionStats || { total: 0, graded: 0 });

      const activities: StudentActivity[] = (dashboardData.submissions || []).map((sub: any) => ({
        id: sub.id,
        lesson_title: (sub.slides as any)?.lessons?.title || 'Unknown Lesson',
        slide_title: (sub.slides as any)?.content?.title || 'Activity',
        score: sub.score,
        graded: sub.graded,
        created_at: sub.created_at,
        feedback: (sub.answer as any)?.feedback
      }));

      setRecentActivities(activities);
    } catch (error) {
      console.error('Error loading student data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDashboardLogin = async () => {
    setAuthAttempted(true);
    if (!sessionCode.trim()) {
      toast.error('Please enter a session code');
      return;
    }
    if (!studentName.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    setAuthLoading(true);
    try {
      const dashboardData = await fetchDashboardData(sessionCode, studentName);
      const session = await createStudentSession(
        dashboardData.studentName,
        dashboardData.session.id,
        dashboardData.session.session_code
      );

      setSessionCode('');
      setStudentName('');
      await loadStudentData(session);
    } catch (error) {
      console.error('Dashboard login error:', error);
      const message = error instanceof Error ? error.message : 'Failed to access dashboard.';
      toast.error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLeaveSession = () => {
    clearStudentSession();
    setStudentSession(null);
    setSessionInfo(null);
    setGrade(null);
    setRecentActivities([]);
    setSubmissionStats({ total: 0, graded: 0 });
    toast.success('Left session');
  };

  const handleContinueSession = () => {
    if (studentSession && sessionInfo?.status === 'active') {
      navigate(`/student/live/${studentSession.sessionId}`);
    }
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 70) return 'text-orange-600';
    if (percentage >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScorePercentage = (score: number | null, maxScore: number) => {
    if (score === null || maxScore === 0) return 0;
    return Math.round((score / maxScore) * 100);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const averageScore = grade?.max_possible_score > 0
    ? Math.round((grade.total_score / grade.max_possible_score) * 100)
    : 0;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!studentSession || !sessionInfo) {
    return (
      <div className="fixed inset-0 bg-[#f8f9fa] flex flex-col">
        {/* Top Bar */}
        <header className="flex items-center justify-between px-4 lg:px-6 h-14 lg:h-16 flex-shrink-0 border-b border-[#e0e0e0] bg-white">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
          >
            <img
              src="/edulive-logo.png"
              alt="EduChemMix"
              className="w-8 h-8 lg:w-9 lg:h-9 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg bg-primary items-center justify-center hidden">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-base lg:text-lg font-semibold text-[#202124]">
              EduChemMix <span className="text-[#5f6368] font-normal text-sm">JHS</span>
            </span>
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-sm text-[#5f6368] hover:text-[#202124]"
          >
            Back to Home
          </Button>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-sm px-8 py-8 lg:px-10 lg:py-10">
              <div className="text-center mb-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <h1 className="text-2xl font-normal text-[#202124] mb-1">
                  Student Dashboard
                </h1>
                <p className="text-sm text-[#5f6368]">
                  Enter your session code and name to view your grades
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <label htmlFor="dashboard-session-code" className="block text-xs font-medium text-[#5f6368] mb-1.5">
                    Session Code
                  </label>
                  <Input
                    id="dashboard-session-code"
                    type="text"
                    value={sessionCode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                      setSessionCode(value);
                    }}
                    placeholder="e.g., ABC123"
                    className="h-12 text-center text-lg font-mono tracking-[0.3em] rounded-md border border-[#dadce0] bg-white focus:border-primary focus:ring-1 focus:ring-primary placeholder:tracking-normal placeholder:font-sans placeholder:text-sm"
                    maxLength={20}
                    disabled={authLoading}
                    autoFocus
                    aria-invalid={authAttempted && !sessionCode.trim()}
                  />
                  {authAttempted && !sessionCode.trim() && (
                    <p className="text-xs text-destructive mt-1.5 text-center font-medium">Required</p>
                  )}
                </div>

                <div>
                  <label htmlFor="dashboard-student-name" className="block text-xs font-medium text-[#5f6368] mb-1.5">
                    Your Full Name
                  </label>
                  <Input
                    id="dashboard-student-name"
                    type="text"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    placeholder="Enter your full name"
                    className="h-12 text-base rounded-md border border-[#dadce0] bg-white focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && !authLoading && handleDashboardLogin()}
                    disabled={authLoading}
                    aria-invalid={authAttempted && !studentName.trim()}
                  />
                  <p className="text-xs text-[#5f6368] mt-1.5">Use the same name you joined with</p>
                  {authAttempted && !studentName.trim() && (
                    <p className="text-xs text-destructive mt-1 font-medium">Required</p>
                  )}
                </div>

                <Button
                  onClick={handleDashboardLogin}
                  className="w-full h-11 rounded-full text-sm font-medium"
                  disabled={authLoading}
                >
                  {authLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      View Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            <p className="text-center text-xs text-[#5f6368] mt-5">
              Want to join a live session?{' '}
              <button
                onClick={() => navigate('/join')}
                className="text-primary hover:underline font-medium"
              >
                Join here
              </button>
            </p>
          </div>
        </main>

        {/* Footer */}
        <footer className="flex items-center justify-center px-4 h-12 flex-shrink-0 border-t border-[#e0e0e0] bg-white">
          <p className="text-xs text-[#5f6368]">
            EduChemMix-JHS &copy; 2026. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-[#e0e0e0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 lg:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {studentSession.studentName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-semibold text-[#202124] truncate">{studentSession.studentName}</h1>
              <p className="text-xs text-[#5f6368] truncate">
                {(sessionInfo as any)?.lessons?.title || 'Student Dashboard'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/student/notifications')}
              className="text-[#5f6368] hover:bg-[#f1f3f4] rounded-full h-9 w-9 p-0"
            >
              <Bell className="w-[18px] h-[18px]" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLeaveSession}
              className="text-[#5f6368] hover:bg-[#f1f3f4] rounded-full text-xs gap-1.5 px-3"
            >
              <LogOut className="w-[18px] h-[18px]" />
              <span className="hidden sm:inline">Switch</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Current Session Card */}
        <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                  sessionInfo.status === 'active'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-[#f1f3f4] text-[#5f6368] border border-[#e0e0e0]'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    sessionInfo.status === 'active' ? 'bg-green-500' : 'bg-[#9aa0a6]'
                  }`} />
                  {sessionInfo.status === 'active' ? 'Live' : 'Ended'}
                </span>
                <span className="text-xs font-mono text-[#5f6368] bg-[#f1f3f4] px-2 py-0.5 rounded">
                  {sessionInfo.session_code}
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-[#202124] mb-3 truncate">
                {(sessionInfo as any)?.lessons?.title || 'Current Lesson'}
              </h2>
              <div className="max-w-md">
                <div className="flex items-center justify-between text-xs text-[#5f6368] mb-1.5">
                  <span>Progress</span>
                  <span className="font-medium text-[#202124]">{grade?.completion_percentage || 0}%</span>
                </div>
                <div className="h-2 bg-[#e0e0e0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${grade?.completion_percentage || 0}%` }}
                  />
                </div>
              </div>
            </div>
            <Button
              className="rounded-full h-10 px-5 text-sm font-medium sm:ml-4 w-full sm:w-auto"
              onClick={handleContinueSession}
              disabled={sessionInfo.status !== 'active'}
            >
              <Play className="w-4 h-4 mr-1.5" />
              {sessionInfo.status === 'active' ? 'Continue' : 'Ended'}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/8 flex items-center justify-center">
                <Star className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs text-[#5f6368]">Overall</span>
            </div>
            <p className={`text-2xl sm:text-3xl font-semibold ${getScoreColor(averageScore)}`}>
              {averageScore}<span className="text-sm font-normal text-[#5f6368]">%</span>
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[#e0e0e0] p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/8 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-xs text-[#5f6368]">Completion</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold text-[#202124]">
              {grade?.completion_percentage || 0}<span className="text-sm font-normal text-[#5f6368]">%</span>
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[#e0e0e0] p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/8 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-xs text-[#5f6368]">Points</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold text-[#202124]">
              {grade?.total_score || 0}
              <span className="text-sm font-normal text-[#5f6368]">/{grade?.max_possible_score || 0}</span>
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[#e0e0e0] p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/8 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-xs text-[#5f6368]">Graded</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold text-[#202124]">
              {submissionStats.graded}
              <span className="text-sm font-normal text-[#5f6368]">/{submissionStats.total}</span>
            </p>
          </div>
        </div>

        {/* Quick Actions + Activities */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Quick Actions - Side */}
          <div className="lg:col-span-1 space-y-3 sm:space-y-4">
            <h3 className="text-xs font-medium text-[#5f6368] uppercase tracking-wider px-1">Quick Actions</h3>
            <button
              onClick={handleContinueSession}
              className="w-full bg-white rounded-xl border border-[#e0e0e0] p-4 hover:border-primary/30 hover:shadow-sm transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/8 flex items-center justify-center group-hover:bg-green-500/12 transition-colors">
                  <Play className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#202124]">Continue Learning</p>
                  <p className="text-xs text-[#5f6368]">Resume your lesson</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/student/notifications')}
              className="w-full bg-white rounded-xl border border-[#e0e0e0] p-4 hover:border-primary/30 hover:shadow-sm transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/8 flex items-center justify-center group-hover:bg-blue-500/12 transition-colors">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#202124]">Notifications</p>
                  <p className="text-xs text-[#5f6368]">Updates & announcements</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/join')}
              className="w-full bg-white rounded-xl border border-[#e0e0e0] p-4 hover:border-primary/30 hover:shadow-sm transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center group-hover:bg-primary/12 transition-colors">
                  <Keyboard className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#202124]">Join New Session</p>
                  <p className="text-xs text-[#5f6368]">Enter a different code</p>
                </div>
              </div>
            </button>
          </div>

          {/* Recent Activities - Main */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-[#e0e0e0]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#e0e0e0]">
                <h3 className="text-sm font-semibold text-[#202124]">Recent Activities</h3>
                <span className="text-xs text-[#5f6368]">{recentActivities.length} submissions</span>
              </div>

              {recentActivities.length === 0 ? (
                <div className="text-center py-12 px-5">
                  <div className="w-12 h-12 rounded-full bg-[#f1f3f4] flex items-center justify-center mx-auto mb-3">
                    <BookOpen className="w-6 h-6 text-[#9aa0a6]" />
                  </div>
                  <p className="text-sm text-[#5f6368]">No activities yet</p>
                  <p className="text-xs text-[#9aa0a6] mt-1">Complete activities to see them here</p>
                </div>
              ) : (
                <div className="divide-y divide-[#e0e0e0]">
                  {recentActivities.map((activity) => {
                    const percentage = activity.score !== null && activity.graded
                      ? getScorePercentage(activity.score, 100)
                      : 0;

                    return (
                      <div
                        key={activity.id}
                        className="flex items-center gap-3 sm:gap-4 px-5 py-3.5 hover:bg-[#f8f9fa] transition-colors cursor-pointer"
                        onClick={() => navigate(`/student/activity/${activity.id}`)}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          activity.graded ? 'bg-primary/8' : 'bg-amber-500/8'
                        }`}>
                          {activity.graded ? (
                            <CheckCircle2 className="w-[18px] h-[18px] text-primary" />
                          ) : (
                            <Clock className="w-[18px] h-[18px] text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#202124] truncate">{activity.slide_title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#5f6368] truncate">{activity.lesson_title}</span>
                            <span className="text-[#dadce0]">&middot;</span>
                            <span className="text-xs text-[#9aa0a6] flex-shrink-0">{formatDate(activity.created_at)}</span>
                          </div>
                          {activity.feedback && (
                            <div className="mt-1.5 flex items-start gap-1.5">
                              <div className="w-1 h-1 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                              <p className="text-xs text-blue-700 line-clamp-1">{activity.feedback}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {activity.graded ? (
                            <span className={`text-base font-semibold ${getScoreColor(percentage)}`}>
                              {percentage}%
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-[#5f6368] bg-[#f1f3f4] px-2 py-0.5 rounded-full">
                              Pending
                            </span>
                          )}
                          <ChevronRight className="w-4 h-4 text-[#9aa0a6]" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}